import { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QueryWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    query: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/queries/submit`, formData);
      toast.success('Thank you! Your query has been submitted. We will contact you soon.');
      setFormData({ name: '', phone: '', email: '', query: '' });
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to submit query. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Query Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full p-4 shadow-2xl spiritual-glow transition-transform hover:scale-110 animate-pulse"
          data-testid="query-widget-btn"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Query Widget */}
      {isOpen && (
        <Card 
          className="fixed bottom-6 right-6 z-50 w-[400px] bg-purple-950/95 backdrop-blur-xl border-2 border-purple-500/40 shadow-2xl spiritual-glow"
          data-testid="query-widget-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 rounded-full p-2">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Have a Question?</h3>
                <p className="text-purple-100 text-xs">Ask us about therapy & healing</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
              data-testid="close-query-widget"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-purple-200 text-sm mb-4">
              Share your questions about quantum healing, therapy sessions, or any concerns. We'll get back to you within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-purple-900/50 border-purple-500/30 text-white placeholder:text-purple-400"
                  data-testid="query-name-input"
                />
              </div>

              <div>
                <Input
                  placeholder="Phone Number *"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  className="bg-purple-900/50 border-purple-500/30 text-white placeholder:text-purple-400"
                  data-testid="query-phone-input"
                />
              </div>

              <div>
                <Input
                  placeholder="Email (Optional)"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-purple-900/50 border-purple-500/30 text-white placeholder:text-purple-400"
                  data-testid="query-email-input"
                />
              </div>

              <div>
                <Textarea
                  placeholder="Your question or concern..."
                  value={formData.query}
                  onChange={(e) => setFormData({...formData, query: e.target.value})}
                  required
                  rows={4}
                  className="bg-purple-900/50 border-purple-500/30 text-white placeholder:text-purple-400 resize-none"
                  data-testid="query-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-6 font-semibold"
                data-testid="submit-query-btn"
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Query
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 pt-4 border-t border-purple-500/30 text-center">
              <p className="text-purple-300 text-xs">
                We respond within 24 hours
              </p>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default QueryWidget;
